// src/pages/Settings.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { PLANS,GLOBAL_PAYMENT_PROVIDER,initiateRazorpaySubscription,initiatePayPalSubscription, GLOBAL_PROVIDERS } from "../services/payment";
import { collection, query, where, getDocs,doc, updateDoc  } from "firebase/firestore";
import { db } from "../services/firebase";
import "./Settings.css";
import { getFunctions, httpsCallable } from "firebase/functions";

const TABS = ["Profile", "Plan & Billing", "Usage"];

export default function Settings() {
  const { user, userData, logout } = useAuth();
  const [activeTab, setActiveTab]       = useState("Profile");
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [stats, setStats] = useState({ comments: 0, ideas: 0, replies: 0, missed: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "yearly"
  const [alert, setAlert] = useState(null);
  
  const plan        = userData?.plan || "free";
  const videosToday = userData?.videosUsedToday || 0;
  const videosMonth = userData?.videosUsedMonth || 0;
  
  const detectDefaultPayment = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = navigator.language || "";
    const isIndia = tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || locale.includes("en-IN");
    return isIndia ? "razorpay" : "paypal";
  };

  const [paymentMethod, setPaymentMethod] = useState(detectDefaultPayment);

  // const [paymentMethod, setPaymentMethod]   = useState("razorpay");


  // Open tab from URL hash
  useEffect(() => {
    const hash = window.location.hash.toLowerCase();

    if (hash === "#plan") {
      setActiveTab("Plan & Billing");
    } else if (hash === "#usage") {
      setActiveTab("Usage");
    } else if (hash === "#profile") {
      setActiveTab("Profile");
    }
  }, []);

  // ── Fetch real stats from Firestore ──────────────────────────
  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const q    = query(collection(db, "analyses"), where("uid", "==", user.uid));
        const snap = await getDocs(q);
        let comments = 0, ideas = 0, replies = 0, missed = 0;
        snap.docs.forEach(d => {
          const data = d.data();
          comments += data.commentsCount                  || 0;
          ideas    += data.ideasCount                     || 0;
          replies  += data.repliesCount                   || 0;
          missed   += data.insights?.missed?.length       || 0;
        });
        setStats({ comments, ideas, replies, missed });
      } catch (err) {
        console.error("Failed to load stats:", err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  // Add this stripe in Settings.js
  useEffect(() => {
  const params        = new URLSearchParams(window.location.search);
  const stripeStatus  = params.get("stripe");
  const paypalStatus  = params.get("paypal");

  // Stripe return
  if (stripeStatus === "success") {
    updateDoc(doc(db, "users", user.uid), {
      plan:            "pro",
      planActivatedAt: new Date().toISOString(),
      videosUsedToday: 0,
      videosUsedMonth: 0,
      lastUsedMonth:   new Date().toISOString().slice(0, 7),
      lastUsedWeek:    "",
    }).then(() => {
      // alert("✅ Pro plan activated via Stripe!");
      showAlert("success", "Pro plan activated via Stripe!");

      window.history.replaceState({}, "", "/settings");
    });
  }
  if (stripeStatus === "cancel") {
    // alert("❌ Stripe payment cancelled.");
    showAlert("error", "Stripe payment cancelled.");

    window.history.replaceState({}, "", "/settings");
  }

  // PayPal return
  if (paypalStatus === "success" && userData?.paypalSubscriptionId && userData?.plan !== "pro") {
    updateDoc(doc(db, "users", user.uid), {
      plan:            "pro",
      planActivatedAt: new Date().toISOString(),
      videosUsedToday: 0,
      videosUsedMonth: 0,
      lastUsedMonth:   new Date().toISOString().slice(0, 7),
      lastUsedWeek:    "",
    }).then(() => {
      // alert("✅ Pro plan activated via PayPal!");
      showAlert("success", "Pro plan activated via PayPal!");

      window.history.replaceState({}, "", "/settings");
    });
  }
  if (paypalStatus === "cancel") {
    // alert("❌ PayPal subscription cancelled.");
    showAlert("error", "PayPal subscription cancelled.");

    window.history.replaceState({}, "", "/settings");
  }
  }, [userData]);

  // ── Payment handler ───────────────────────────────────────────
const handleUpgrade = async (targetPlan, cycle = "monthly") => {
  if (paymentLoading) return;
  setPaymentLoading(targetPlan);
  
  const functions = getFunctions();
  try {
    const planData = PLANS[targetPlan];

    if (paymentMethod === "razorpay") {

      const razorpayPlanId = cycle === "yearly"
        ? planData.razorpayYearlyPlanId
        : planData.razorpayPlanId;

      if (!razorpayPlanId) {
        // alert(`❌ Razorpay Plan ID missing for ${cycle} cycle`);
        showAlert("error", `Razorpay Plan ID missing for ${cycle} cycle`);

        setPaymentLoading(null);
        return;
      }

      const createSub = httpsCallable(functions, "createRazorpaySubscription");
      const result    = await createSub({ planId: razorpayPlanId,cycle });

       if (result.data.success === false) {
        // alert(`❌ ${result.data.description || result.data.error}`);
        showAlert("error", result.data.description || result.data.error);

        setPaymentLoading(null);
        return;
      }


      const subscriptionId = result.data.subscriptionId;

      // 2. Save subscriptionId to user doc
      await updateDoc(doc(db, "users", user.uid), {
        razorpaySubscriptionId: subscriptionId,
      });

      // 3. Open Razorpay checkout
      initiateRazorpaySubscription({
        planId:    subscriptionId,
        planName:  planData.name,
        userEmail: user.email,
        userName:  user.displayName,
        onSuccess: async (response) => {
          await updateDoc(doc(db, "users", user.uid), {
            plan:                   "pro",
            billingCycle:           cycle,
            planActivatedAt:        new Date().toISOString(),
            razorpayPaymentId:      response.razorpay_payment_id,
            razorpaySubscriptionId: response.razorpay_subscription_id,
            videosUsedToday:        0,
            videosUsedMonth:        0,
            lastUsedMonth:          new Date().toISOString().slice(0, 7),
            lastUsedWeek:           "",
          });
          // alert("✅ Pro plan activated!");
          showAlert("success", "Pro plan activated!");

          setPaymentLoading(null);
        },
        onFailure: (reason) => {
          // alert(`❌ Payment failed: ${reason}`);
          showAlert("error", `Payment failed: ${reason}`);

          setPaymentLoading(null);
        },
      });
    // ── PayPal ────────────────────────────────────────────────
    } else if (paymentMethod === "paypal") {
      const planId = cycle === "yearly"
        ? planData.paypalYearlyPlanId
        : planData.paypalPlanId;

      if (!planId || planId.startsWith("P-XXX")) {
        // alert("❌ PayPal Plan ID not configured");
        showAlert("error", "PayPal Plan ID not configured");

        setPaymentLoading(null);
        return;
      }

      const createSub = httpsCallable(functions, "createPayPalSubscription");
      const result    = await createSub({ planId, cycle });

      if (result.data.success === false) {
        // alert(`❌ ${result.data.description || result.data.error}`);
        showAlert("error", result.data.description || result.data.error);

        setPaymentLoading(null);
        return;
      }

      await updateDoc(doc(db, "users", user.uid), {
        paypalSubscriptionId: result.data.subscriptionId,
        billingCycle:         cycle,
      });

      window.location.href = result.data.approvalUrl;

    // ── Stripe ────────────────────────────────────────────────
    } else if (paymentMethod === "stripe") {
      const planId = cycle === "yearly"
        ? planData.stripeYearlyPlanId
        : planData.stripePlanId;

      if (!planId || planId.startsWith("price_XXX")) {
        // alert("❌ Stripe Plan ID not configured");
        showAlert("error", "Stripe Plan ID not configured");

        setPaymentLoading(null);
        return;
      }

      const createSession = httpsCallable(functions, "createStripeSubscription");
      const result        = await createSession({
        planId,
        email: user.email,
        name:  user.displayName,
      });

      if (result.data.success === false) {
        // alert(`❌ ${result.data.description || result.data.error}`);
        showAlert("error", result.data.description || result.data.error);

        setPaymentLoading(null);
        return;
      }

      await updateDoc(doc(db, "users", user.uid), {
        stripeSessionId: result.data.sessionId,
        billingCycle:    cycle,
      });

      window.location.href = result.data.sessionUrl;
    }



  } catch (err) {
    // alert(`❌ Error: ${err.message}`);
    showAlert("error", `Error: ${err.message}`);

    setPaymentLoading(null);
  }
};

const showAlert = (type, msg) => {
  setAlert({ type, msg });
  setTimeout(() => setAlert(null), 4000);
};

  return (
    <div className="page-wrap settings-page">

      {alert && (
        <div style={{
          position: "fixed",
          top: 24, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: alert.type === "success" ? "#0f2a1e" : "#2a0f0f",
          border: `1px solid ${alert.type === "success" ? "#22c55e44" : "#ef444444"}`,
          borderRadius: 12,
          padding: "12px 18px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          minWidth: 280, maxWidth: 420,
          animation: "slideDown 0.3s cubic-bezier(0.34,1.4,0.64,1) both",
        }}>
          <span style={{ fontSize: 18 }}>
            {alert.type === "success" ? "✅" : "❌"}
          </span>
          <span style={{ fontSize: 14, color: "#fff", flex: 1 }}>{alert.msg}</span>
          <button onClick={() => setAlert(null)} style={{
            background: "none", border: "none",
            color: "#666", fontSize: 16,
            cursor: "pointer", padding: "0 4px",
          }}>✕</button>
          <style>{`
            @keyframes slideDown {
              from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}
      

      <h1 className="settings-title fade-up">Settings</h1>

      {/* Tabs */}
      <div className="settings-tabs fade-up">
        {TABS.map(tab => (
          <button key={tab} className={`settings-tab ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      {/* ─────────────────── PROFILE TAB ─────────────────── */}
      {activeTab === "Profile" && (
        <div className="tab-content fade-up">
          <div className="card profile-card">
            <img
              src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.displayName}&background=6f6af8&color=fff`}
              alt="avatar" className="profile-avatar"
            />
            <div className="profile-info">
              <h2 className="profile-name">{user?.displayName}</h2>
              <div className="profile-email">{user?.email}</div>
              <div className="profile-badges">
                <span className={`badge ${plan === "pro" ? "badge-accent" : "badge-green"}`}>
                  {plan === "pro" ? "✦ Pro" : "Free Plan"}
                </span>
                <span className="badge" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>Google Account</span>
              </div>
            </div>
          </div>
          <div className="card" style={{ marginTop: 16 }}>
            <div className="section-title">Account</div>
            <div className="settings-row">
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Display Name</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{user?.displayName}</div>
              </div>
              <span className="badge" style={{ background: "var(--surface2)", color: "var(--text-dim)", fontSize: 11 }}>Via Google</span>
            </div>
            <div className="settings-divider" />
            <div className="settings-row">
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Email</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{user?.email}</div>
              </div>
              <span className="badge" style={{ background: "var(--surface2)", color: "var(--text-dim)", fontSize: 11 }}>Verified</span>
            </div>
            <div className="settings-divider" />
            <div className="settings-row danger-row">
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Sign Out</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>You'll be signed out of CreatorComment</div>
              </div>
              <button className="btn btn-danger" onClick={logout}>Sign Out</button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────── PLAN & BILLING TAB ─────────────────── */}
      {activeTab === "Plan & Billing" && (
        <div className="tab-content fade-up">
          <div className="card payment-method-card">
            <div className="section-title">Payment Method</div>

              <div className="payment-toggle">
                <button
                  className={`payment-opt ${paymentMethod === "razorpay" ? "active" : ""}`}
                  onClick={() => setPaymentMethod("razorpay")}
                >
                  <span>₹</span> Razorpay
                  <span style={{ fontSize: 11, color: "var(--text-dim)" }}>(India · UPI)</span>
                </button>

                {GLOBAL_PROVIDERS.paypal && (
                  <button
                    className={`payment-opt ${paymentMethod === "paypal" ? "active" : ""}`}
                    onClick={() => setPaymentMethod("paypal")}
                  >
                    <span style={{ color: "#009cde" }}>P</span> PayPal
                    <span style={{ fontSize: 11, color: "var(--text-dim)" }}>(Global · Cards)</span>
                  </button>
                )}

                {GLOBAL_PROVIDERS.stripe && (
                  <button
                    className={`payment-opt ${paymentMethod === "stripe" ? "active" : ""}`}
                    onClick={() => setPaymentMethod("stripe")}
                  >
                    <span style={{ color: "#6772e5" }}>S</span> Stripe
                    <span style={{ fontSize: 11, color: "var(--text-dim)" }}>(Global · Cards)</span>
                  </button>
                )}
              </div>

          </div>
          <div className="plans-grid">
            {/* Free Plan */}
            {/* <div className={`card plan-card ${plan === "free" ? "current-plan" : ""}`}>
              <div className="plan-header">
                <div>
                  <div className="plan-name">Free</div>
                  <div className="plan-price"><span className="price-amount">₹0</span><span className="price-period">/month</span></div>
                </div>
                {plan === "free" && <span className="badge badge-green">Current</span>}
              </div>
              <div className="plan-limit-bar">
                <div className="limit-label">
                  <span>Videos used today</span>
                  <span>{videosToday} / {PLANS.free.weeklyLimit}</span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${Math.min((videosToday / PLANS.free.weeklyLimit) * 100, 100)}%` }} />
                </div>
              </div>
              <ul className="plan-features">
                {PLANS.free.features.map(f => (
                  <li key={f.text} className={f.included ? "" : "feat-missing"}>
                    <span className={f.included ? "feat-check" : "feat-x"}>{f.included ? "✓" : "✗"}</span>{f.text}
                  </li>
                ))}
              </ul>
              <button className="btn btn-ghost" disabled style={{ width: "100%", justifyContent: "center", opacity: 0.5 }}>Free Plan Active</button>
            </div> */}
            {/* Pro Plan */}
            <div className={`card plan-card pro-plan ${plan === "pro" ? "current-plan" : ""}`}>
              {/* <div className="plan-badge-top">Most Popular ✦</div> */}

              {/* ── Billing cycle toggle ── */}
              <div className="billing-cycle-toggle">
                <button
                  className={`cycle-btn ${billingCycle === "monthly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("monthly")}
                >
                  Monthly
                </button>
                <button
                  className={`cycle-btn ${billingCycle === "yearly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("yearly")}
                >
                  Yearly
                  <span className="cycle-save-badge">Save 20%</span>
                </button>
              </div>

              <div className="plan-header">
                <div>
                  <div className="plan-name">Pro</div>
                  <div className="plan-price">
                    <span className="price-amount">
                      {paymentMethod === "razorpay"
                        ? billingCycle === "yearly" ? `₹${PLANS.pro.priceINRYearly}` : `₹${PLANS.pro.priceINR}`
                        : billingCycle === "yearly" ? `$${PLANS.pro.priceUSDYearly}` : `$${PLANS.pro.priceUSD}`
                      }
                    </span>
                    <span className="price-period">/{billingCycle === "yearly" ? "year" : "month"}</span>
                  </div>
                  {billingCycle === "yearly" && (
                    <div className="price-monthly-equiv">
                      ≈ {paymentMethod === "razorpay" ? `₹${Math.round(PLANS.pro.priceINRYearly / 12)}/mo` : `$${Math.round(PLANS.pro.priceUSDYearly / 12)}/mo`}
                      {" "}<span className="save-tag">⭐ Save 20%</span>
                    </div>
                  )}
                </div>
                {plan === "pro" && <span className="badge badge-accent">Current</span>}
              </div>

              {plan === "pro" && (
                <div className="plan-limit-bar">
                  <div className="limit-label">
                    <span>Videos this month</span>
                    <span>{videosMonth} / {PLANS.pro.monthlyLimit}</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${Math.min((videosMonth / PLANS.pro.monthlyLimit) * 100, 100)}%` }} />
                  </div>
                </div>
              )}

              <ul className="plan-features">
                {PLANS.pro.features.map(f => (
                  <li key={f.text}><span className="feat-check accent">✓</span>{f.text}</li>
                ))}
              </ul>

              {plan === "pro" ? (
                <button className="btn btn-ghost" disabled style={{ width: "100%", justifyContent: "center", opacity: 0.5 }}>
                  Pro Active
                </button>
              ) : (
                <button
                  className="btn btn-primary upgrade-btn"
                  onClick={() => handleUpgrade("pro", billingCycle)}
                  disabled={!!paymentLoading}
                >
                  {paymentLoading === "pro"
                    ? <><span className="spinner" /> Processing...</>
                    : `✦ Upgrade via ${
                        paymentMethod === "razorpay" ? "Razorpay" :
                        paymentMethod === "paypal"   ? "PayPal"   : "Stripe"
                      }`
                  }
                </button>
              )}
            </div>
          </div>
          <div className="billing-note">
            <span>🔒</span>
            <span>Payments are processed securely via {paymentMethod === "razorpay" ? "Razorpay" : "PayPal"}. Cancel anytime. No hidden charges.</span>
          </div>
        </div>
      )}

      {/* ─────────────────── USAGE TAB ─────────────────── */}
      {activeTab === "Usage" && (
        <div className="tab-content fade-up">
          <div className="usage-grid">
            <div className="card usage-stat-card">
              <div className="usage-label">Videos Used Today</div>
              <div className="usage-number">{videosToday}</div>
              <div className="usage-limit">of {plan === "free" ? "5" : "∞"} weekly limit</div>
              {plan === "free" && (
                <div className="progress-track" style={{ marginTop: 12 }}>
                  <div className="progress-fill" style={{ width: `${Math.min((videosToday / 3) * 100, 100)}%` }} />
                </div>
              )}
            </div>
            {/* <div className="card usage-stat-card">
              <div className="usage-label">Videos This Month</div>
              <div className="usage-number">{videosMonth}</div>
              <div className="usage-limit">of {plan === "free" ? "30" : "100"} monthly limit</div>
              {plan === "free" && (
                <div className="progress-track" style={{ marginTop: 12 }}>
                  <div className="progress-fill accent2" style={{ width: `${Math.min((videosMonth / 30) * 100, 100)}%` }} />
                </div>
              )}
            </div> */}
            <div className="card usage-stat-card">
              <div className="usage-label">Current Plan</div>
              <div className="usage-number" style={{ fontSize: 28 }}>{plan === "pro" ? "Pro ✦" : "Free"}</div>
              <div className="usage-limit">{plan === "pro" ? "Full access enabled" : "Limited insights"}</div>
              {plan === "free" && (
                <button className="btn btn-primary" style={{ marginTop: 14, fontSize: 13 }} onClick={() => setActiveTab("Plan & Billing")}>
                  ✦ Upgrade to Pro
                </button>
              )}
            </div>
          </div>

          {/* ── Real stats from Firestore ── */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="section-title">All-Time Activity</div>
            <div className="usage-breakdown">
              {statsLoading ? (
                <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading stats...</div>
              ) : (
                <>
                  {[
                    { label: "Comments analyzed",     value: stats.comments.toLocaleString(), icon: "💬" },
                    { label: "Video ideas generated", value: stats.ideas,                     icon: "💡" },
                    { label: "Replies drafted",       value: stats.replies,                   icon: "✍️" },
                    { label: "Opportunities found",   value: stats.missed,                    icon: "🚨" },
                  ].map(row => (
                    <div key={row.label} className="breakdown-row">
                      <span>{row.icon} {row.label}</span>
                      <strong>{row.value}</strong>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}